import api from 'api'
import {
  CACHE_TAG_ACCOUNTS_VERSION_LIST,
  CACHE_TAG_ACCOUNT_DETAILS_BY_ID,
  CACHE_TAG_ACCOUNT_LIST,
  CACHE_TAG_ACCOUNT_EXPANDABLE_VIEW,
  CACHE_TAG_CONFIGURED_SERVICES_LIST,
  CACHE_TAG_CONFIGURED_SERVICE_DETAILS,
  CACHE_TAG_WORK_ORDER_LIST,
} from 'api/cacheTagTypes'

import type { ReactivateAccountApi, ReactivateAccountBody } from 'types/api/account/accountReactivation'

type ReactivateAccountArgs = ReactivateAccountBody & { accountId: string }

const accountTags = (accountId: string) => [
  { type: CACHE_TAG_ACCOUNTS_VERSION_LIST, id: accountId },
  { type: CACHE_TAG_ACCOUNT_DETAILS_BY_ID, id: accountId },
  CACHE_TAG_ACCOUNT_LIST,
]

const restoredTags = (accountId: string) => [
  ...accountTags(accountId),
  CACHE_TAG_ACCOUNT_EXPANDABLE_VIEW,
  CACHE_TAG_CONFIGURED_SERVICES_LIST,
  // The services are restored by the request itself; only their stops arrive later.
  CACHE_TAG_CONFIGURED_SERVICE_DETAILS,
  CACHE_TAG_WORK_ORDER_LIST,
]

// A schedule restores nothing yet, so the account's services and work orders, the heaviest lists here, stay cached.
export const reactivationTags = (result: ReactivateAccountApi | undefined, accountId: string) =>
  result?.status === 'SCHEDULED' ? accountTags(accountId) : restoredTags(accountId)

// A 404 means the schedule is gone: a run or someone's immediate reactivation may have restored the services already.
export const cancelScheduleTags = (error: { status?: unknown } | undefined, accountId: string) =>
  error?.status === 404 ? restoredTags(accountId) : accountTags(accountId)

const extendedApi = api.injectEndpoints({
  endpoints: build => ({
    reactivateAccountWithCapture: build.mutation<ReactivateAccountApi, ReactivateAccountArgs>({
      query: ({ accountId, ...body }) => ({
        url: `/api/v1/core/accounts/${accountId}/lifecycle/reactivate`,
        method: 'POST',
        body,
      }),
      // A refused request changed nothing, so the dialog's lists have nothing to refetch.
      invalidatesTags: (result, error, { accountId }) => (error ? [] : reactivationTags(result, accountId)),
    }),
    cancelScheduledReactivation: build.mutation<void, { accountId: string }>({
      query: ({ accountId }) => ({
        url: `/api/v1/core/accounts/${accountId}/lifecycle/scheduled-reactivation`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { accountId }) => cancelScheduleTags(error, accountId),
    }),
  }),
})

export const { useReactivateAccountWithCaptureMutation, useCancelScheduledReactivationMutation } = extendedApi
